import { ACTIVE_STAGES } from "./config";

/**
 * Diagnostic de l'appel découverte de 15 minutes, saisi dans la fiche du contact
 * et stocké sur le deal (colonne deals.diagnostic, JSON). Les questions et les
 * libellés viennent du guide d'appel : une variante entreprise, une variante indépendant.
 */

export type DiagnosticMode = "ent" | "ind";

export const QUESTION_IDS = ["q1", "q2", "q3", "q4", "q5", "q6"] as const;
export type QuestionId = (typeof QUESTION_IDS)[number];

export const CHECKLIST_IDS = ["c1", "c2", "c3", "c4", "c5"] as const;
export type ChecklistId = (typeof CHECKLIST_IDS)[number];

export type DiagnosticCalcul = {
  heures: number | null;
  personnes: number | null;
  tarif: number | null;
  semaines: number | null;
};

export type Diagnostic = {
  version: 1;
  mode: DiagnosticMode;
  /** q6 porte le frein exprimé par le prospect, sa note est dans `importance`. */
  reponses: Record<QuestionId, string>;
  calcul: DiagnosticCalcul;
  importance: number | null;
  checklist: Record<ChecklistId, boolean>;
  levier: string;
  prochaine_etape: string;
  /** Deal note holding the last exported report, so a second export updates it instead of duplicating. */
  note_id: number | null;
};

export type Question = {
  id: QuestionId;
  etape: string;
  question: string;
  ecouter: string[];
  relance: string;
};

export type ChecklistItem = { id: ChecklistId; label: string; court: string };

const SEMAINES_PAR_DEFAUT = 46;

export const DIAGNOSTIC_QUESTIONS: Record<DiagnosticMode, Question[]> = {
  ent: [
    {
      id: "q1",
      etape: "Situation",
      question:
        "Aujourd'hui, qui utilise l'IA chez vous, pour faire quoi, et avec quels outils ?",
      ecouter: [
        "Personne ne s'en sert",
        "Un seul s'en sert, les autres regardent",
        "Chacun bricole avec son compte gratuit",
      ],
      relance: "Et les autres dans l'équipe, ils font comment ?",
    },
    {
      id: "q2",
      etape: "Irritant",
      question:
        "Sur une semaine type, quelles sont les deux ou trois tâches qui vous prennent le plus de temps sans rien rapporter ?",
      ecouter: [
        "Devis, relances, comptes rendus",
        "Mails, saisie, recherche d'informations",
        "Contenu, posts, fiches produit",
      ],
      relance:
        "Laquelle vous agace le plus ? Garde une seule tâche pour la suite.",
    },
    {
      id: "q3",
      etape: "Coût",
      question:
        "Pour cette tâche, combien de temps par semaine, et combien de personnes la font ?",
      ecouter: [
        "Un chiffre en heures, même approximatif",
        "Le nombre de personnes concernées",
      ],
      relance:
        "À combien vous valorisez l'heure ? Puis dis le total à voix haute.",
    },
    {
      id: "q4",
      etape: "Déjà essayé",
      question:
        "Qu'avez-vous déjà essayé pour gagner ce temps, et qu'est-ce qui a fait que ça n'a pas tenu ?",
      ecouter: [
        "On n'a pas de méthode",
        "Les résultats sont moyens",
        "Pas le temps de se former",
        "L'équipe n'a pas suivi",
      ],
      relance:
        "Qu'est-ce qui a manqué, d'après vous : l'outil, le temps, ou quelqu'un pour montrer comment faire ?",
    },
    {
      id: "q5",
      etape: "Conséquence",
      question:
        "Si rien ne change d'ici un an, qu'est-ce que ça donne, pour vous et pour l'équipe ?",
      ecouter: [
        "Fatigue, surcharge, départs",
        "Retard sur des concurrents qui s'y mettent",
        "Erreurs, délais, clients mécontents",
      ],
      relance:
        "Et côté données, vos équipes savent-elles ce qu'elles ont le droit de coller dans un outil d'IA ?",
    },
    {
      id: "q6",
      etape: "Importance",
      question:
        "Sur une échelle de 1 à 10, à quel point c'est important pour vous de régler ça cette année ?",
      ecouter: [
        "7 ou plus : l'urgence est là",
        "5 ou 6 : il y a un frein, cherche-le",
        "Moins de 5 : la douleur est trop faible, reviens à la question 3",
      ],
      relance:
        "Pourquoi pas un chiffre plus bas ? Puis : qu'est-ce qui vous empêche de mettre un point de plus ? Note le frein mot pour mot.",
    },
  ],
  ind: [
    {
      id: "q1",
      etape: "Situation",
      question:
        "Aujourd'hui, comment utilisez-vous l'IA dans votre activité, pour faire quoi, et avec quels outils ?",
      ecouter: [
        "J'essaie de temps en temps",
        "ChatGPT gratuit, sans méthode",
        "Je n'ai pas le temps de m'y mettre",
      ],
      relance:
        "Quand vous l'utilisez, ça vous fait gagner du temps ou ça vous en fait perdre ?",
    },
    {
      id: "q2",
      etape: "Irritant",
      question:
        "Sur une semaine type, quelles sont les deux ou trois tâches qui vous prennent du temps sans que vous puissiez les facturer ?",
      ecouter: [
        "Devis, facturation, relances de paiement",
        "Prospection, réseaux sociaux, site",
        "Comptes rendus, mails, administratif",
      ],
      relance:
        "Laquelle vous agace le plus ? Garde une seule tâche pour la suite.",
    },
    {
      id: "q3",
      etape: "Coût",
      question:
        "Pour cette tâche, combien d'heures par semaine, et combien facturez-vous l'heure quand vous travaillez pour un client ?",
      ecouter: [
        "Un nombre d'heures, même approximatif",
        "Son tarif horaire ou journalier réel",
      ],
      relance:
        "Si ces heures étaient facturées, ça ferait combien par mois ? Puis dis le total à voix haute.",
    },
    {
      id: "q4",
      etape: "Déjà essayé",
      question:
        "Qu'avez-vous déjà essayé pour récupérer ces heures, et qu'est-ce qui a fait que ça n'a pas tenu ?",
      ecouter: [
        "Pas le temps d'apprendre seul",
        "Des tutos suivis puis abandonnés",
        "Les résultats sont génériques",
        "Je ne sais pas par où commencer",
      ],
      relance:
        "Combien d'heures avez-vous déjà passées à essayer seul, au total ? Ces heures sont déjà un coût.",
    },
    {
      id: "q5",
      etape: "Conséquence",
      question:
        "Si rien ne change d'ici un an, qu'est-ce que ça donne pour votre activité ? Et parmi vos confrères, lesquels l'utilisent déjà ?",
      ecouter: [
        "Des confrères qui répondent plus vite",
        "Des devis envoyés en retard",
        "Épuisement, soirées et week-ends travaillés",
      ],
      relance:
        "Qu'est-ce que ces confrères peuvent faire, que vous ne pouvez pas faire aujourd'hui ? Laisse-le décrire l'écart.",
    },
    {
      id: "q6",
      etape: "Importance",
      question:
        "Sur une échelle de 1 à 10, à quel point c'est important pour vous de régler ça cette année ?",
      ecouter: [
        "7 ou plus : l'urgence est là",
        "5 ou 6 : il y a un frein, cherche-le",
        "Moins de 5 : la douleur est trop faible, reviens à la question 3",
      ],
      relance:
        "Pourquoi pas un chiffre plus bas ? Puis : qu'est-ce qui vous empêche de mettre un point de plus ? Note le frein mot pour mot.",
    },
  ],
};

export const CHECKLIST_ITEMS: Record<DiagnosticMode, ChecklistItem[]> = {
  ent: [
    {
      id: "c1",
      label: "Qui décide, et est-ce lui ou elle seul·e ?",
      court: "Décideur identifié",
    },
    {
      id: "c2",
      label: "Combien de personnes à former, au total",
      court: "Nombre de personnes confirmé",
    },
    {
      id: "c3",
      label: "Financement envisagé : OPCO, FIFPL, AGEFICE ou fonds propres",
      court: "Financement abordé",
    },
    {
      id: "c4",
      label: "Période souhaitée pour la formation",
      court: "Période souhaitée connue",
    },
    {
      id: "c5",
      label: "Prochaine étape fixée avec une date",
      court: "Prochaine étape datée",
    },
  ],
  ind: [
    {
      id: "c1",
      label:
        "Statut connu (libéral, micro-entrepreneur, artisan, commerçant) pour le bon financement",
      court: "Statut connu",
    },
    {
      id: "c2",
      label: "Seul, ou avec des confrères pour partager le prix",
      court: "Venue seul ou à plusieurs",
    },
    {
      id: "c3",
      label: "Financement abordé : FIFPL, AGEFICE ou fonds propres",
      court: "Financement abordé",
    },
    {
      id: "c4",
      label: "Période souhaitée pour la formation",
      court: "Période souhaitée connue",
    },
    {
      id: "c5",
      label: "Prochaine étape fixée avec une date",
      court: "Prochaine étape datée",
    },
  ],
};

/** Libellés de la vue d'ensemble, hors questions du guide. */
export const MODE_LABELS: Record<DiagnosticMode, string> = {
  ent: "Entreprise",
  ind: "Indépendant",
};

export function diagnosticVide(mode: DiagnosticMode): Diagnostic {
  return {
    version: 1,
    mode,
    reponses: { q1: "", q2: "", q3: "", q4: "", q5: "", q6: "" },
    calcul: {
      heures: null,
      personnes: mode === "ind" ? 1 : null,
      tarif: null,
      semaines: SEMAINES_PAR_DEFAUT,
    },
    importance: null,
    checklist: { c1: false, c2: false, c3: false, c4: false, c5: false },
    levier: "",
    prochaine_etape: "",
    note_id: null,
  };
}

export function modeParDefaut(secteur?: string | null): DiagnosticMode {
  return secteur === "independant" ? "ind" : "ent";
}

const isObj = (x: unknown): x is Record<string, unknown> =>
  typeof x === "object" && x !== null && !Array.isArray(x);
const nombre = (x: unknown): number | null =>
  typeof x === "number" && Number.isFinite(x) && x >= 0 ? x : null;

/** Lit le JSON de la base sans jamais planter : ce qui n'est pas valide retombe sur le vide. */
export function normaliserDiagnostic(
  brut: unknown,
  modeDefaut: DiagnosticMode,
): Diagnostic {
  const source = isObj(brut) ? brut : {};
  const mode: DiagnosticMode =
    source.mode === "ent" || source.mode === "ind" ? source.mode : modeDefaut;
  const d = diagnosticVide(mode);

  if (isObj(source.reponses)) {
    for (const id of QUESTION_IDS) {
      const v = source.reponses[id];
      if (typeof v === "string") d.reponses[id] = v;
    }
  }
  if (isObj(source.calcul)) {
    for (const cle of ["heures", "personnes", "tarif", "semaines"] as const) {
      const v = nombre(source.calcul[cle]);
      if (v !== null) d.calcul[cle] = v;
    }
  }
  if (isObj(source.checklist)) {
    for (const id of CHECKLIST_IDS) {
      if (source.checklist[id] === true) d.checklist[id] = true;
    }
  }
  const imp = source.importance;
  d.importance =
    typeof imp === "number" && Number.isInteger(imp) && imp >= 1 && imp <= 10
      ? imp
      : null;
  if (typeof source.levier === "string") d.levier = source.levier;
  if (typeof source.prochaine_etape === "string")
    d.prochaine_etape = source.prochaine_etape;
  const noteId = source.note_id;
  d.note_id =
    typeof noteId === "number" && Number.isInteger(noteId) && noteId > 0
      ? noteId
      : null;
  return d;
}

export type Cout = {
  heuresAn: number;
  euroAn: number | null;
  euroMois: number | null;
  euroSemaine: number | null;
};

export function calculerCout(calcul: DiagnosticCalcul): Cout | null {
  const { heures, personnes, tarif } = calcul;
  if (!heures || !personnes) return null;
  const semaines = calcul.semaines || SEMAINES_PAR_DEFAUT;
  const heuresAn = heures * personnes * semaines;
  if (!tarif) {
    return { heuresAn, euroAn: null, euroMois: null, euroSemaine: null };
  }
  return {
    heuresAn,
    euroAn: heuresAn * tarif,
    euroMois: (heuresAn * tarif) / 12,
    euroSemaine: heures * personnes * tarif,
  };
}

/** Nombre arrondi avec une espace simple comme séparateur de milliers (même rendu partout). */
export function formaterNombre(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function phraseCout(cout: Cout): string {
  let s = `${formaterNombre(cout.heuresAn)} heures par an`;
  if (cout.euroAn !== null && cout.euroMois !== null) {
    s += `, soit environ ${formaterNombre(cout.euroAn)} euros par an (${formaterNombre(cout.euroMois)} euros par mois)`;
  }
  return s;
}

const ETAPES_RAPPORT: [QuestionId, string][] = [
  ["q1", "1. Situation"],
  ["q2", "2. Irritant"],
  ["q3", "3. Coût"],
  ["q4", "4. Déjà essayé"],
  ["q5", "5. Conséquence"],
];

export function texteCompteRendu(
  d: Diagnostic,
  contexte: { contact?: string; entreprise?: string; formation?: string },
): string {
  const lignes = [
    `Appel découverte de 15 min (${d.mode === "ind" ? "indépendant" : "entreprise"})`,
  ];
  const prospect = [contexte.contact, contexte.entreprise]
    .filter(Boolean)
    .join(", ");
  if (prospect) lignes.push(`Prospect : ${prospect}`);
  if (contexte.formation) lignes.push(`Demande : ${contexte.formation}`);
  lignes.push("");

  for (const [id, etiquette] of ETAPES_RAPPORT) {
    lignes.push(`${etiquette} : ${d.reponses[id].trim() || "non abordé"}`);
    if (id === "q3") {
      const cout = calculerCout(d.calcul);
      if (cout) lignes.push(`   Calcul : ${phraseCout(cout)}`);
    }
  }
  lignes.push(
    `Importance : ${d.importance !== null ? `${d.importance} sur 10` : "non abordée"}`,
  );
  if (d.reponses.q6.trim()) lignes.push(`Frein : ${d.reponses.q6.trim()}`);
  if (d.levier.trim()) {
    lignes.push("");
    lignes.push(`Levier utilisé : ${d.levier.trim()}`);
  }
  lignes.push("");
  const valides = CHECKLIST_ITEMS[d.mode]
    .filter((item) => d.checklist[item.id])
    .map((item) => item.court);
  lignes.push(`Points validés : ${valides.join(", ") || "aucun"}`);
  if (d.prochaine_etape.trim())
    lignes.push(`Prochaine étape : ${d.prochaine_etape.trim()}`);
  return lignes.join("\n");
}

type DealPourChoix = {
  id: number | string;
  stage: string;
  created_at: string;
  archived_at?: string | null;
};

/** La demande en cours la plus récente : ni archivée, ni gagnée, réalisée ou perdue. */
export function choisirDealEnCours<T extends DealPourChoix>(
  deals: T[],
): T | null {
  const enCours = deals
    .filter((d) => !d.archived_at && ACTIVE_STAGES.includes(d.stage))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  return enCours[0] ?? null;
}
